import { useState } from 'react';
import { Minus, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CATEGORIAS, type Produto } from '@/api/endpoints/orcamentos';
import { brl, type OrcamentoResultado } from '../calc';
import { Field, StepHeader, Thumb } from '../ui';
import { inputCls, linkBtnCls, nf, pct } from '../styles';

interface KitLine {
  produtoId: number;
  quantidade: number;
}

const custoLabel = (p: Produto) =>
  p.moedaCusto === 'USD' ? `US$ ${p.custoUsd.toLocaleString('pt-BR')}` : brl(p.custoBrl);

/**
 * Catálogo inteiro à vista, agrupado por categoria: cada produto se adiciona
 * com um clique e a quantidade é ajustada na própria linha. Brinco segue o
 * número de animais do passo anterior.
 */
export function KitStep({
  produtos,
  loading,
  kit,
  animais,
  desconto,
  resultado,
  onAdd,
  onRemove,
  onQtd,
  onDescontoChange,
}: {
  produtos: Produto[];
  loading: boolean;
  kit: KitLine[];
  animais: number;
  desconto: { pct: number; reais: number };
  resultado: OrcamentoResultado;
  onAdd: (id: number) => void;
  onRemove: (id: number) => void;
  onQtd: (id: number, q: number) => void;
  onDescontoChange: (d: { pct: number; reais: number }) => void;
}) {
  const [showDesconto, setShowDesconto] = useState(desconto.pct > 0 || desconto.reais > 0);
  const qtdById = new Map(kit.map((k) => [k.produtoId, k.quantidade]));
  const temBrinco = produtos.some((p) => p.categoria === 'brinco' && qtdById.has(p.id));

  const grupos = CATEGORIAS.map((c) => ({
    ...c,
    itens: produtos.filter((p) => p.categoria === c.value),
  })).filter((g) => g.itens.length > 0);

  return (
    <div>
      <StepHeader
        title="Monte o kit"
        description="Escolha o que vai na proposta. Leitor e aplicador entram com custo de importação e margem; brinco tem preço fechado."
      />

      {loading ? (
        <p className="py-6 text-sm text-text-secondary">Carregando catálogo…</p>
      ) : grupos.length === 0 ? (
        <p className="py-6 text-sm text-text-secondary">
          Nenhum produto ativo no catálogo. Cadastre na aba Catálogo de produtos.
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          {grupos.map((g) => (
            <section key={g.value}>
              <h3 className="mb-2 text-sm font-medium text-text-body">{g.label}</h3>
              <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-muted">
                {g.itens.map((p) => {
                  const qtd = qtdById.get(p.id);
                  const noKit = qtd !== undefined;
                  const ehBrinco = p.categoria === 'brinco';
                  return (
                    <li
                      key={p.id}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 transition-colors',
                        noKit && 'bg-primary/15'
                      )}
                    >
                      <Thumb url={p.imagemUrl} className="size-10" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-text-title">
                          {p.nome}
                          {p.modelo && !p.nome.includes(p.modelo) && (
                            <span className="font-normal text-text-secondary"> {p.modelo}</span>
                          )}
                        </p>
                        <p className="text-xs text-text-secondary">
                          {custoLabel(p)} {ehBrinco ? 'por brinco' : 'de custo'}
                          {noKit && ehBrinco && animais > 0 && qtd === animais && (
                            <span className="text-text-secondary"> · 1 por animal</span>
                          )}
                        </p>
                      </div>

                      {noKit ? (
                        <div className="flex animate-pop items-center gap-1">
                          <QtdStepper value={qtd} onChange={(q) => onQtd(p.id, q)} />
                          <button
                            type="button"
                            onClick={() => onRemove(p.id)}
                            aria-label={`Tirar ${p.nome} do kit`}
                            className="rounded-md p-1.5 text-text-secondary transition-colors hover:bg-muted hover:text-error"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onAdd(p.id)}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-sm font-medium text-text-body transition-colors hover:border-primary hover:text-primary"
                        >
                          <Plus size={14} /> Adicionar
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}

      {temBrinco && (
        <div className="mt-6">
          {showDesconto ? (
            <div className="rounded-lg border border-border bg-muted p-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-medium text-text-title">Desconto nos brincos</p>
                <button
                  type="button"
                  className={linkBtnCls}
                  onClick={() => {
                    onDescontoChange({ pct: 0, reais: 0 });
                    setShowDesconto(false);
                  }}
                >
                  tirar desconto
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Em porcentagem">
                  <input
                    type="number"
                    min={0}
                    className={inputCls}
                    value={desconto.pct}
                    onChange={(e) => onDescontoChange({ ...desconto, pct: nf(e.target.value) })}
                  />
                </Field>
                <Field label="Em reais por brinco">
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    className={inputCls}
                    value={desconto.reais}
                    onChange={(e) => onDescontoChange({ ...desconto, reais: nf(e.target.value) })}
                  />
                </Field>
              </div>
              {resultado.brincoDesconto > 0 && (
                <p className="mt-3 text-sm text-text-secondary">
                  {resultado.brincoQtd} brincos saem por{' '}
                  <span className="font-medium text-text-title">
                    {brl(resultado.brincoLiquido)}
                  </span>{' '}
                  em vez de {brl(resultado.brincoBruto)}
                  {desconto.pct > 0 && ` (${pct(desconto.pct)} off)`}.
                </p>
              )}
            </div>
          ) : (
            <button type="button" className={linkBtnCls} onClick={() => setShowDesconto(true)}>
              Dar desconto nos brincos
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function QtdStepper({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center rounded-md border border-border bg-bg-main">
      <button
        type="button"
        aria-label="Diminuir"
        onClick={() => onChange(Math.max(1, value - 1))}
        className="p-1.5 text-text-secondary hover:text-text-title"
      >
        <Minus size={14} />
      </button>
      <input
        aria-label="Quantidade"
        inputMode="numeric"
        className="w-14 bg-transparent text-center text-sm text-text-title outline-none"
        value={value}
        onChange={(e) => onChange(nf(e.target.value.replace(/\D/g, '')))}
      />
      <button
        type="button"
        aria-label="Aumentar"
        onClick={() => onChange(value + 1)}
        className="p-1.5 text-text-secondary hover:text-text-title"
      >
        <Plus size={14} />
      </button>
    </div>
  );
}
